import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import * as authPb from '../generated/fitness-service/v1/auth_pb';
import { getAuthServicePromiseClient } from '../utils/grpcClient';

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface SignupCredentials {
    name: string;
    email: string;
    password: string;
    role: string;
    trainerCode?: string;
    agencyCode?: string;
    // Agency-specific fields (optional, used when role = "agency")
    description?: string;
    address?: string;
    contactPhone?: string;
    // Trainer-specific fields (optional, used when role = "trainer")
    phone?: string;
    specialization?: string;
    certification?: string;
    experienceYears?: number;
}

interface User {
    id: string;
    email: string;
    role: string;
    name?: string;
    message?: string;
}

interface AuthContextType {
    isAuthenticated: boolean;
    accessToken: string | null;
    refreshToken: string | null;
    user: User | null;
    isLoading: boolean;
    error: string | null;
    login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string }>;
    signup: (credentials: SignupCredentials) => Promise<{ success: boolean; error?: string }>;
    logout: () => Promise<void>;
    refreshTokens: () => Promise<{ success: boolean; error?: string }>;
    clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to validate JWT token
const isTokenValid = (token: string | null): boolean => {
    if (!token) return false;

    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        return payload.exp > currentTime;
    } catch {
        return false;
    }
};

// Helper function to handle gRPC errors
const handleGrpcError = (error: any): string => {
    console.log('handleGrpcError called with:', error);

    // Handle gRPC-Web errors properly
    if (error && typeof error === 'object') {
        // gRPC-Web errors can have different structures
        let grpcCode = null;
        let grpcMessage = null;

        // Check for gRPC status code in various formats
        if (error.code !== undefined) {
            grpcCode = error.code;
        } else if (error.grpcCode !== undefined) {
            grpcCode = error.grpcCode;
        } else if (error.status !== undefined) {
            grpcCode = error.status;
        }

        // Check for gRPC message in various formats
        if (error.message) {
            grpcMessage = error.message;
        } else if (error.grpcMessage) {
            grpcMessage = error.grpcMessage;
        } else if (error.statusText) {
            grpcMessage = error.statusText;
        }

        // Handle gRPC status codes according to the official gRPC specification
        if (grpcCode !== null) {
            switch (grpcCode) {
                case 0: // OK
                    return 'Operation completed successfully';
                case 1: // CANCELLED
                    return 'Operation was cancelled';
                case 2: // UNKNOWN
                    return 'Unknown error occurred';
                case 3: // INVALID_ARGUMENT
                    return grpcMessage || 'Invalid input provided. Please check your credentials.';
                case 4: // DEADLINE_EXCEEDED
                    return 'Request timed out. Please try again.';
                case 5: // NOT_FOUND
                    return 'Requested resource not found';
                case 6: // ALREADY_EXISTS
                    return grpcMessage || 'Resource already exists';
                case 7: // PERMISSION_DENIED
                    return 'Access denied. Please check your permissions.';
                case 8: // RESOURCE_EXHAUSTED
                    return 'Too many requests. Please wait before trying again.';
                case 9: // FAILED_PRECONDITION
                    return 'Request cannot be executed in the current state';
                case 10: // ABORTED
                    return 'Operation was aborted';
                case 11: // OUT_OF_RANGE
                    return 'Operation attempted past valid range';
                case 12: // UNIMPLEMENTED
                    return 'Operation is not implemented or supported';
                case 13: // INTERNAL
                    return grpcMessage || 'Internal server error. Please try again later.';
                case 14: // UNAVAILABLE
                    return 'Service is temporarily unavailable. Please try again later.';
                case 15: // DATA_LOSS
                    return 'Data loss or corruption detected';
                case 16: // UNAUTHENTICATED
                    return grpcMessage || 'Invalid email or password. Please check your credentials.';
                default:
                    return grpcMessage || `Service error (${grpcCode}). Please try again.`;
            }
        }

        // If we have a message but no code, return the message
        if (grpcMessage) {
            return grpcMessage;
        }
    }

    // Handle array-based error responses (legacy)
    if (Array.isArray(error)) {
        const errorMessage = error.find(item => typeof item === 'string' && item.length > 0);
        if (errorMessage) {
            return errorMessage;
        }
    }

    // Handle network errors
    if (error?.message?.includes('UNAVAILABLE') || error?.message?.includes('fetch')) {
        return 'Service is currently unavailable. Please check your connection and try again.';
    }

    // Handle timeout errors
    if (error?.message?.includes('timeout') || error?.message?.includes('DEADLINE_EXCEEDED')) {
        return 'Request timed out. Please check your connection and try again.';
    }

    // Handle connection errors
    if (error?.message?.includes('connection') || error?.message?.includes('Connection') ||
        error?.message?.includes('port closed')) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    // Extract meaningful error messages from any remaining structure
    if (error?.message && typeof error.message === 'string') {
        // Clean up common error prefixes
        let cleanMessage = error.message
            .replace(/^Error: /, '')
            .replace(/^RpcError: /, '')
            .replace(/^\d+ /, '') // Remove error codes at the start
            .trim();

        if (cleanMessage && cleanMessage !== 'An unexpected error occurred') {
            return cleanMessage;
        }
    }

    return 'Unable to authenticate. Please check your credentials and try again.';
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [accessToken, setAccessToken] = useState<string | null>(
        localStorage.getItem('authToken')
    );
    const [refreshToken, setRefreshToken] = useState<string | null>(
        localStorage.getItem('refreshToken')
    );
    const [user, setUser] = useState<User | null>(() => {
        const storedUser = localStorage.getItem('currentUser');
        return storedUser ? JSON.parse(storedUser) : null;
    });
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [tokenExp, setTokenExp] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const logout = useCallback(async () => {
        try {
            // Call backend logout endpoint if tokens exist
            if (accessToken && refreshToken) {
                try {
                    const request = new authPb.LogoutRequest();
                    request.setAccessToken(accessToken);
                    request.setRefreshToken(refreshToken);

                    const authServiceClient = getAuthServicePromiseClient();

                    // Add timeout for logout request
                    const timeoutPromise = new Promise((_, reject) => {
                        setTimeout(() => reject(new Error('Logout request timed out')), 5000);
                    });

                    await Promise.race([
                        authServiceClient.logout(request, {}),
                        timeoutPromise
                    ]);

                } catch (backendError) {
                    // Continue with local cleanup even if backend call fails
                }
            }
        } catch (error) {
            // Continue with local cleanup even if there are errors
        } finally {
            try {
                // Always clear local session
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('currentUser');
            } catch (storageError) {
                // Error clearing localStorage during logout - continue anyway
            }

            // Always reset state
            setAccessToken(null);
            setRefreshToken(null);
            setUser(null);
            setTokenExp(null);
        }
    }, [accessToken, refreshToken]);

    const decodeToken = useCallback((token: string) => {
        try {
            const [, payloadBase64] = token.split(".");
            const decodedJson = atob(payloadBase64);
            const decodedJsonPayload = JSON.parse(decodedJson);

            setTokenExp(decodedJsonPayload.exp);
        } catch (error) {
            // Clear session on decode error
            logout();
        }
    }, [logout]);

    const login = useCallback(async (credentials: LoginCredentials) => {
        setIsLoading(true);
        setError(null); // Clear any previous errors

        // Enhanced input validation
        if (!credentials.email?.trim() || !credentials.password) {
            const errorMsg = 'Please provide both email and password.';
            setIsLoading(false);
            setError(errorMsg);
            return {
                success: false,
                error: errorMsg
            };
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(credentials.email.trim())) {
            const errorMsg = 'Please provide a valid email address.';
            setIsLoading(false);
            setError(errorMsg);
            return {
                success: false,
                error: errorMsg
            };
        }

        if (credentials.password.length < 3) {
            const errorMsg = 'Password is too short.';
            setIsLoading(false);
            setError(errorMsg);
            return {
                success: false,
                error: errorMsg
            };
        }

        try {
            const request = new authPb.LoginRequest();
            request.setEmail(credentials.email.trim().toLowerCase());
            request.setPassword(credentials.password);

            const authServiceClient = getAuthServicePromiseClient();

            const metadata = {}; // Default metadata

            // Add timeout wrapper for the request
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Login request timed out after 10 seconds')), 10000);
            });

            // Race between the actual request and timeout
            const response = await Promise.race([
                authServiceClient.login(request, metadata),
                timeoutPromise
            ]) as any;

            // Validate response existence
            if (!response) {
                const errorMsg = 'No response received from server. Please try again.';
                setError(errorMsg);
                return {
                    success: false,
                    error: errorMsg
                };
            }

            // Extract response fields safely
            let accessToken, refreshToken, userId, role;

            try {
                accessToken = response.getAccessToken?.() || response.accessToken;
                refreshToken = response.getRefreshToken?.() || response.refreshToken;
                userId = response.getUserId?.() || response.userId;
                role = response.getRole?.() || response.role;
            } catch (extractError) {
                const errorMsg = 'Invalid response format from server. Please try again.';
                setError(errorMsg);
                return {
                    success: false,
                    error: errorMsg
                };
            }

            // Validate required fields
            if (!accessToken || !refreshToken || !userId) {
                const errorMsg = 'Invalid credentials or server error. Please check your email and password.';
                setError(errorMsg);
                return {
                    success: false,
                    error: errorMsg
                };
            }

            // Validate token format
            if (typeof accessToken !== 'string' || !accessToken.includes('.') || accessToken.split('.').length !== 3) {
                const errorMsg = 'Server returned invalid authentication token. Please try again.';
                setError(errorMsg);
                return {
                    success: false,
                    error: errorMsg
                };
            }

            // Create user data
            try {
                const userData = {
                    id: userId,
                    email: credentials.email.trim().toLowerCase(),
                    role: role || 'user',
                    message: response.getMessage?.() || ''
                };

                // Store tokens and user session
                localStorage.setItem('authToken', accessToken);
                localStorage.setItem('refreshToken', refreshToken);
                localStorage.setItem('currentUser', JSON.stringify(userData));

                // Update context state
                setAccessToken(accessToken);
                setRefreshToken(refreshToken);
                setUser(userData);
                decodeToken(accessToken);
                setError(null); // Clear any errors on successful login

                return { success: true };

            } catch (storageError) {
                const errorMsg = 'Failed to save login session. Please try again.';
                setError(errorMsg);
                return {
                    success: false,
                    error: errorMsg
                };
            }

        } catch (error: any) {
            // Check if this is actually a successful response that we failed to parse
            if (error && typeof error === 'object' && !error.message && !error.code) {
                // Try to extract tokens from the error object
                try {
                    const accessToken = error.getAccessToken?.() || error.accessToken;
                    const refreshToken = error.getRefreshToken?.() || error.refreshToken;
                    const userId = error.getUserId?.() || error.userId;

                    if (accessToken && refreshToken && userId) {
                        const userData = {
                            id: userId,
                            email: credentials.email.trim().toLowerCase(),
                            role: error.getRole?.() || error.role || 'user',
                            message: error.getMessage?.() || ''
                        };

                        localStorage.setItem('authToken', accessToken);
                        localStorage.setItem('refreshToken', refreshToken);
                        localStorage.setItem('currentUser', JSON.stringify(userData));

                        setAccessToken(accessToken);
                        setRefreshToken(refreshToken);
                        setUser(userData);
                        decodeToken(accessToken);
                        setError(null);

                        return { success: true };
                    }
                } catch (parseError) {
                    // Failed to parse potential success response
                }
            }

            const errorMsg = handleGrpcError(error);
            setError(errorMsg);

            return {
                success: false,
                error: errorMsg
            };
        } finally {
            setIsLoading(false);
        }
    }, [decodeToken]);

    const signup = useCallback(async (credentials: SignupCredentials) => {
        setIsLoading(true);
        try {
            const request = new authPb.SignupRequest();
            request.setName(credentials.name);
            request.setEmail(credentials.email);
            request.setPassword(credentials.password);
            request.setRole(credentials.role);

            if (credentials.trainerCode) {
                request.setTrainerCode(credentials.trainerCode);
            }
            if (credentials.agencyCode) {
                request.setAgencyCode(credentials.agencyCode);
            }

            // Set agency-specific fields if provided
            if (credentials.description) {
                request.setDescription(credentials.description);
            }
            if (credentials.address) {
                request.setAddress(credentials.address);
            }
            if (credentials.contactPhone) {
                request.setContactPhone(credentials.contactPhone);
            }

            // Set trainer-specific fields if provided
            if (credentials.phone) {
                request.setPhone(credentials.phone);
            }
            if (credentials.specialization) {
                request.setSpecialization(credentials.specialization);
            }
            if (credentials.certification) {
                request.setCertification(credentials.certification);
            }
            if (credentials.experienceYears !== undefined) {
                request.setExperienceYears(credentials.experienceYears);
            }

            const authServiceClient = getAuthServicePromiseClient();
            const metadata = {}; // Default metadata

            // Use the promise client
            const response = await authServiceClient.signup(request, metadata);

            if (response) {
                const accessToken = response.getAccessToken();
                const refreshToken = response.getRefreshToken();
                const userId = response.getUserId();
                const role = response.getRole();
                const message = response.getMessage();

                if (accessToken && refreshToken && userId) {
                    const userData = {
                        id: userId,
                        name: credentials.name,
                        email: credentials.email,
                        role: role,
                        message: message
                    };

                    // Store tokens and user session
                    localStorage.setItem('authToken', accessToken);
                    localStorage.setItem('refreshToken', refreshToken);
                    localStorage.setItem('currentUser', JSON.stringify(userData));

                    setAccessToken(accessToken);
                    setRefreshToken(refreshToken);
                    setUser(userData);
                    decodeToken(accessToken);

                    return { success: true };
                } else {
                    return {
                        success: false,
                        error: 'Invalid response from server - missing tokens or user ID'
                    };
                }
            } else {
                return {
                    success: false,
                    error: 'No response received from server'
                };
            }
        } catch (error: any) {
            // Handle special case for agency signup pending approval
            if (credentials.role === 'agency' && error &&
                (error.code === 7 || error.grpcCode === 7 || error.status === 7)) {
                // gRPC code 7 = PERMISSION_DENIED, which we use for pending approval
                const message = error.message || error.grpcMessage || 'Agency application submitted - pending admin approval';
                return {
                    success: true,
                    message: message
                };
            }

            return {
                success: false,
                error: handleGrpcError(error)
            };
        } finally {
            setIsLoading(false);
        }
    }, [decodeToken]);

    const refreshTokens = useCallback(async () => {
        if (!refreshToken) {
            await logout();
            return { success: false, error: 'No refresh token available. Please log in again.' };
        }

        try {
            const request = new authPb.RefreshTokenRequest();
            request.setRefreshToken(refreshToken);

            const authServiceClient = getAuthServicePromiseClient();
            const metadata = {}; // Default metadata

            // Add timeout for refresh request
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Token refresh timed out after 8 seconds')), 8000);
            });

            const response = await Promise.race([
                authServiceClient.refreshToken(request, metadata),
                timeoutPromise
            ]) as any;

            if (!response) {
                await logout();
                return { success: false, error: 'Session expired. Please log in again.' };
            }

            const newAccessToken = response.getAccessToken();
            const newRefreshToken = response.getRefreshToken();
            const userId = response.getUserId();
            const role = response.getRole();

            if (!newAccessToken || !newRefreshToken) {
                await logout();
                return { success: false, error: 'Session expired. Please log in again.' };
            }

            try {
                // Update user data if provided
                const updatedUser = user ? {
                    ...user,
                    id: userId || user.id,
                    role: role || user.role,
                    message: response.getMessage()
                } : null;

                // Store new tokens
                localStorage.setItem('authToken', newAccessToken);
                localStorage.setItem('refreshToken', newRefreshToken);
                if (updatedUser) {
                    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
                }

                setAccessToken(newAccessToken);
                setRefreshToken(newRefreshToken);
                setUser(updatedUser);
                decodeToken(newAccessToken);

                return { success: true };

            } catch (storageError) {
                await logout();
                return { success: false, error: 'Failed to save refreshed session. Please log in again.' };
            }

        } catch (error: any) {
            // Clear session on refresh failure
            await logout();

            // Provide user-friendly error message
            let errorMessage = 'Session expired. Please log in again.';
            if (error?.message?.includes('timeout')) {
                errorMessage = 'Session refresh timed out. Please log in again.';
            } else if (error?.message?.includes('UNAUTHENTICATED')) {
                errorMessage = 'Session expired. Please log in again.';
            } else if (error?.message?.includes('connection')) {
                errorMessage = 'Connection error during session refresh. Please check your internet and log in again.';
            }

            return { success: false, error: errorMessage };
        }
    }, [refreshToken, user, logout, decodeToken]);

    const isAuthenticated = useMemo(() => {
        if (!accessToken || !user) return false;

        // Check if token is still valid
        if (tokenExp && tokenExp * 1000 <= Date.now()) {
            return false;
        }

        return isTokenValid(accessToken);
    }, [accessToken, user, tokenExp]);

    // Initialize auth state from localStorage on mount
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const storedAccessToken = localStorage.getItem('authToken');
                const storedRefreshToken = localStorage.getItem('refreshToken');
                const storedUserJson = localStorage.getItem('currentUser');

                let storedUser = null;
                try {
                    storedUser = storedUserJson ? JSON.parse(storedUserJson) : null;
                } catch (parseError) {
                    // Clear corrupted data
                    localStorage.removeItem('currentUser');
                }

                if (storedAccessToken && storedRefreshToken && storedUser) {
                    if (isTokenValid(storedAccessToken)) {
                        // Access token is still valid
                        setAccessToken(storedAccessToken);
                        setRefreshToken(storedRefreshToken);
                        setUser(storedUser);
                        decodeToken(storedAccessToken);
                    } else {
                        // Access token expired, try to refresh
                        setAccessToken(null);
                        setRefreshToken(storedRefreshToken);
                        setUser(storedUser);

                        try {
                            const refreshResult = await refreshTokens();
                            if (!refreshResult.success) {
                                // Refresh failed, clear session
                                await logout();
                            }
                        } catch (error) {
                            await logout();
                        }
                    }
                } else {
                    // No valid session, clear any partial data
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('currentUser');
                    setAccessToken(null);
                    setRefreshToken(null);
                    setUser(null);
                    setTokenExp(null);
                }
            } catch (error) {
                // Clear everything on error
                try {
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('currentUser');
                } catch (storageError) {
                    // Error clearing localStorage
                }
                setAccessToken(null);
                setRefreshToken(null);
                setUser(null);
                setTokenExp(null);
            } finally {
                // Always set initialLoading to false
                setInitialLoading(false);
            }
        };

        initializeAuth();
    }, []); // Empty dependency array - only run on mount

    // Auto refresh token when it's about to expire
    useEffect(() => {
        if (!isAuthenticated || !tokenExp || !refreshToken) return;

        const timeUntilExpiry = (tokenExp * 1000) - Date.now();
        const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 0); // Refresh 5 minutes before expiry

        if (refreshTime > 0) {
            const timeoutId = setTimeout(async () => {
                try {
                    const refreshResult = await refreshTokens();
                    if (!refreshResult.success) {
                        // Auto-refresh failed
                    }
                } catch (error) {
                    // Auto-refresh error
                }
            }, refreshTime);

            return () => clearTimeout(timeoutId);
        } else if (timeUntilExpiry > 0) {
            // Token is already expired or will expire very soon, refresh immediately
            refreshTokens().catch(error => {
                // Immediate refresh error
            });
        }
    }, [isAuthenticated, tokenExp, refreshToken, refreshTokens]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                accessToken,
                refreshToken,
                user,
                isLoading: isLoading || initialLoading,
                error,
                login,
                signup,
                logout,
                refreshTokens,
                clearError
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
